import axios from 'axios';
import { EditorTheme } from '@nx-frontend/shared/types';

interface ShareShort {
  globalId: string;
  displayName: string;
  mime: string;
  dateAdded: number;
  dateUpdated: number;
  noteGlobalId: string;
  type: string;
  role: string;
  extra: { height: number; width: number; type: string };
  isScreenshot: boolean;
  size: number;
  inList: boolean;
  storedFileUUID: string;
  isEncrypted: boolean;
  workspaceId: string;
  org: {
    globalId: string;
    userId: number;
  };
}

interface WorkspaceIdPayload {
  gatewayUrl: string;
  shareId: string;
  securityKey: string;
  noteId: string;
}

export const getWorkspaceId = async (payload: WorkspaceIdPayload): Promise<string> => {
  try {
    const { gatewayUrl, shareId, securityKey, noteId } = payload;
    if (shareId && securityKey && noteId) {
      const response = await axios.get(`${gatewayUrl}/ft:notes/shares/${shareId}/key/${securityKey}/note/${noteId}`, {
        headers: {
          'accept': 'application/json, text/plain, */*',
          'content-type': 'application/json',
        },
      });
      if (response.data) {
        const share: ShareShort = response.data;
        return share.workspaceId;
      }
    }
  } catch (err) {
    console.error(err);
  }
  return '';
};

/**
 * Load link file once
 */
export const loadLink = ({
  id,
  url,
  rel,
  type,
  as,
}: {
  url: string;
  id: string;
  rel: string;
  type?: string;
  as?: string;
}): void => {
  if (!document.getElementById(id)) {
    const link = document.createElement('link');
    link.rel = rel;
    type && (link.type = type);
    as && (link.as = as);
    link.href = url;
    link.id = id;
    document.head.appendChild(link);
  }
};

/**
 * Load css file once
 */
export const loadCss = ({ id, url }: { url: string; id: string }): void => {
  if (!document.getElementById(id)) {
    loadLink({ id, rel: 'stylesheet', type: 'text/css', url });
  }
};

/**
 * Load js file once
 */
export const loadJs = ({ id, url }: { url: string; id: string }): void => {
  if (!document.getElementById(id)) {
    const script = document.createElement('script');
    script.src = url;
    script.id = id;
    script.async = true;
    document.body.appendChild(script);
  }
};

/**
 * Load lottie files
 */
export function loadLottie(): void {
  loadJs({ url: 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.10.0/lottie.min.js', id: 'lottie-bundle' });
  loadLink({
    url: 'https://d3jlxgnskmmqem.cloudfront.net/diamond-animation/animation-blue.json',
    id: 'lottie-animation-blue-json',
    rel: 'preload',
    as: 'fetch',
  });
  loadLink({
    url: 'https://d3jlxgnskmmqem.cloudfront.net/diamond-animation/animation-yellow.json',
    id: 'lottie-animation-yellow-json',
    rel: 'preload',
    as: 'fetch',
  });
  loadLink({
    url: 'https://d3jlxgnskmmqem.cloudfront.net/animations/confetti-horizontal.json',
    id: 'lottie-animation-confetti-horizontal',
    rel: 'preload',
    as: 'fetch',
  });
}

/**
 * Proxy matchMedia change theme
 * @param event
 */
export const proxyMatchMediaChangeTheme = (event: MediaQueryListEvent): void => {
  const newColorScheme: EditorTheme = event.matches ? 'dark' : 'light';
  window.dispatchEvent(new CustomEvent('editor:matchMediaThemeChange', { detail: newColorScheme }));
};
